<template>
	<div>
		<div 
			:class="{ 'dense': dense, 'outlined': outlined, 'disabled': disabled }"
		>
			<div v-if="label && !hideDetails">
				{{ label }}<span v-if="required">*</span>
			</div>
			<input
				ref="phoneInput"
				type="tel"
				:placeholder="placeholder"
				:disabled="disabled"
				:required="required"
				:name="name"
			/>
			<div v-if="!hideDetails && hasError" class="phone-error-messages">
				<div v-for="error in errorMessagesArray" :key="error">
					{{ error }}
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import intlTelInput from 'intl-tel-input';
import 'intl-tel-input/build/css/intlTelInput.css';
import utils from 'intl-tel-input/build/js/utils.js';

export default {
	name: 'PhoneInput',
	props: {
		value: {
			type: String,
			required: false,
			default: '',
		},
		label: {
			type: String,
			required: false,
			default: '',
		},
		placeholder: {
			type: String,
			required: false,
			default: '',
		},
		outlined: {
			type: Boolean,
			required: false,
			default: true,
		},
		dense: {
			type: Boolean,
			required: false,
			default: true,
		},
		hideDetails: {
			type: Boolean,
			required: false,
			default: true,
		},
		errorMessages: {
			type: [String, Array],
			required: false,
			default: () => [],
		},
		disabled: {
			type: Boolean,
			required: false,
			default: false,
		},
		clearable: {
			type: Boolean,
			required: false,
			default: false,
		},
		required: {
			type: Boolean,
			required: false,
			default: false,
		},
		name: {
			type: String,
			required: false,
			default: '',
		},
		defaultCountry: {
			type: String,
			required: false,
			default: 'tr',
		},
		preferredCountries: {
			type: Array,
			required: false,
			default: () => ['tr', 'us', 'gb', 'de'],
		},
	},
	data() {
		return {
			iti: null,
			currentValue: '',
			isInitializing: false,
		};
	},
	computed: {
		errorMessagesArray() {
			if (Array.isArray(this.errorMessages)) {
				return this.errorMessages;
			}
			return this.errorMessages ? [this.errorMessages] : [];
		},
		hasError() {
			return this.errorMessagesArray.length > 0;
		},
	},
	methods: {
		initializeIntlTelInput() {
			if (this.$refs.phoneInput && !this.iti) {
				this.iti = intlTelInput(this.$refs.phoneInput, {
					initialCountry: this.defaultCountry.toLowerCase(),
					preferredCountries: this.preferredCountries.map(c => c.toLowerCase()),
					separateDialCode: true,
					showSelectedDialCode: true,
					autoPlaceholder: 'aggressive',
					formatOnDisplay: true,
					nationalMode: false,
					utilsScript: utils
				});

				// Add event listeners
				this.$refs.phoneInput.addEventListener('input', this.handleInput);
				this.$refs.phoneInput.addEventListener('countrychange', this.handleCountryChange);
			}
		},
		handleInput() {
			if (this.iti) {
				const number = this.iti.getNumber();
				this.currentValue = number;
				this.$emit('input', number);
			}
		},
		handleCountryChange() {
			if (this.iti && !this.isInitializing) {
				// Only clear the input when country changes manually, not during initialization
				this.$refs.phoneInput.value = '';
				this.currentValue = '';
				this.$emit('input', '');
				
				const countryData = this.iti.getSelectedCountryData();
				this.$emit('country-changed', countryData);
			}
		},
		focus() {
			if (this.$refs.phoneInput) {
				this.$refs.phoneInput.focus();
			}
		},
		blur() {
			if (this.$refs.phoneInput) {
				this.$refs.phoneInput.blur();
			}
		},
		setNumber(number) {
			if (this.iti && number !== this.currentValue) {
				this.isInitializing = true;
				this.iti.setNumber(number);
				this.currentValue = number;
				
				// Force country selection after setting number to override
				// intl-tel-input's automatic country detection when multiple
				// countries share the same calling code (e.g., GB vs JE for +44)
				this.$nextTick(() => {
					if (this.defaultCountry) {
						this.iti.setCountry(this.defaultCountry.toLowerCase());
					}
					this.isInitializing = false;
				});
			}
		},
		setCountry(countryCode) {
			if (this.iti && countryCode) {
				this.isInitializing = true;
				this.iti.setCountry(countryCode.toLowerCase());
				this.$nextTick(() => {
					this.isInitializing = false;
				});
			}
		},
		getNumber() {
			return this.iti ? this.iti.getNumber() : '';
		},
		getValidationError() {
			return this.iti ? this.iti.getValidationError() : null;
		},
		isValidNumber() {
			return this.iti ? this.iti.isValidNumber() : false;
		},
		destroy() {
			if (this.iti) {
				this.$refs.phoneInput.removeEventListener('input', this.handleInput);
				this.$refs.phoneInput.removeEventListener('countrychange', this.handleCountryChange);
				this.iti.destroy();
				this.iti = null;
			}
		},
	},
	mounted() {
		this.$nextTick(() => {
			this.initializeIntlTelInput();
			if (this.value) {
				this.setNumber(this.value);
			}
		});
	},
	beforeDestroy() {
		this.destroy();
	},
	watch: {
		value: {
			handler(newValue) {
				if (newValue !== this.currentValue) {
					this.setNumber(newValue || '');
				}
			},
			immediate: false,
		},
		disabled(newValue) {
			if (this.$refs.phoneInput) {
				this.$refs.phoneInput.disabled = newValue;
			}
		},
		defaultCountry: {
			handler(newCountry, oldCountry) {
				// Update country when defaultCountry prop changes
				// This handles cases where country is detected after component initialization
				if (newCountry && newCountry !== oldCountry && this.iti) {
					this.setCountry(newCountry);
				}
			},
			immediate: false,
		},
	},
};
</script>

<style scoped>
.phone-input-wrapper {
	width: 100%;
}

.phone-input-label {
	font-size: 12px;
	color: rgba(0, 0, 0, 0.6);
	margin-bottom: 4px;
	font-weight: 400;
}

.phone-input-label span {
	color: #f44336;
	margin-left: 2px;
}

.error-messages {
	margin-top: 4px;
}

.error-messages div {
	font-size: 12px;
	color: #f44336;
	line-height: 1.4;
}
</style>

<style>
/* Match Vuetify v-text-field styling */
.iti {
	width: 100%;
	position: relative;
}

.iti input[type=tel] {
	width: 100%;
	height: 56px;
	padding: 0 12px;
	border: 1px solid rgba(0, 0, 0, 0.23);
	border-radius: 4px;
	font-size: 16px;
	font-family: 'Roboto', sans-serif;
	background-color: transparent !important;
	transition: border-color 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
	box-sizing: border-box;
}

.iti input[type=tel]:focus {
	outline: none;
	border-color: #1976d2;
	border-width: 2px;
}

.iti__flag-container {
	height: 100%;
	border: 1px solid rgba(0, 0, 0, 0.23);
	border-right: none;
	border-bottom: none;
	border-radius: 4px 0 0 4px;
	background-color: transparent !important;
	transition: border-color 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.iti__country-list {
	padding-left: 0 !important;
}

.iti input[type=tel]:focus + .iti__flag-container,
.iti--focused .iti__flag-container {
	border-color: #1976d2;
	border-width: 2px;
}

/* Dense style */
.dense .iti input[type=tel] {
	height: 40px;
	font-size: 14px;
}

.dense .iti__flag-container {
	height: 38px;
}

/* Error styling for phone input */
.phone-error-messages {
	margin-top: 4px;
}

.phone-error-messages div {
	font-size: 12px;
	color: #f44336;
	line-height: 1.4;
}

/* Apply error border when there are errors */
.v-input--has-state .iti input[type=tel] {
	border-color: #f44336 !important;
}

.v-input--has-state .iti__flag-container {
	border-color: #f44336 !important;
}

.theme--dark .iti__dropdown-content {
	background-color: #424242;
	border-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .iti--inline-dropdown .iti__dropdown-content {
	border: 1px solid rgba(255, 255, 255, 0.12);
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
}

.theme--dark .iti__search-input {
	background-color: #424242;
	color: rgba(255, 255, 255, 0.87);
}

.theme--dark .iti__search-input::placeholder {
	color: rgba(255, 255, 255, 0.38);
}

.theme--dark .iti__search-input + .iti__country-list {
	border-top-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .iti__country-list {
	background-color: #424242;
}

.theme--dark .iti__country {
	color: rgba(255, 255, 255, 0.87);
}

.theme--dark .iti__country-name {
	color: rgba(255, 255, 255, 0.87);
}

.theme--dark .iti__dial-code {
	color: rgba(255, 255, 255, 0.6);
}

.theme--dark .iti__country.iti__highlight {
	background-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .iti__divider {
	border-bottom-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .iti__arrow {
	border-top-color: rgba(255, 255, 255, 0.7);
}

.theme--dark .iti__arrow--up {
	border-bottom-color: rgba(255, 255, 255, 0.7);
}

.theme--dark .iti input[type=tel] {
	border-color: rgba(255, 255, 255, 0.23);
	color: rgba(255, 255, 255, 0.87);
}

.theme--dark .iti input[type=tel]::placeholder {
	color: rgba(255, 255, 255, 0.38);
}

.theme--dark .iti input[type=tel]:focus {
	border-color: #1976d2;
}

.theme--dark .iti__flag-container {
	border-color: rgba(255, 255, 255, 0.23);
}

.theme--dark .iti--focused .iti__flag-container,
.theme--dark .iti input[type=tel]:focus + .iti__flag-container {
	border-color: #1976d2;
}
</style>
